const localStorage = window.localStorage;

export const FacebookResponseTypes = {
  AUTH: 'auth',
  CONNECT: 'connect',
  ERROR: 'error',
  STATUS: 'status',
  POST: 'post',
  IMAGE_UPLOAD: 'image_upload',
  VIDEO_UPLOAD: 'video_upload'
};

export default class FacebookApi {
  constructor(socket, apiUrl, callback) {
    this.lsKey = 'luci_fb_user';

    this.callback = callback;
    this.apiUrl = apiUrl;
    this.popup = null;
    this.socket = socket;
    this.clientId = null;
    this.user = this.getLocalStorageUser();

    this.initWebSocket();
  }

  /**
   * Sends response to callback.
   */
  sendResponse = response => {
    if (this.callback) {
      this.callback(response);
    }
  };

  /**
   * Gets necessary headers for api calls.
   */
  getHeader = pageId => {
    const headers = {
      'Content-Type': 'application/json'
    };

    if (this.user) {
      const pageAccessToken = pageId ? this.getAccessTokenForPageById(pageId) : null;
      headers.Authorization = `Bearer ${pageAccessToken || this.user.accessToken}`;
      headers.FbUserId = this.user.id;
    }

    if (this.clientId) {
      headers.ClientId = this.clientId;
    }

    return headers;
  };

  isAuthenticated = () => !!this.user;

  /**
   * Gets the user from local store
   */
  getLocalStorageUser = () => {
    const user = getLocalStorageObject(this.lsKey);
    const now = new Date();

    if (user) {
      user.expiration = new Date(user.expiration);
      if (now >= user.expiration) {
        localStorage.removeItem(this.lsKey);
      } else {
        this.user = user;
      }
      this.sendResponse({
        type: FacebookResponseTypes.AUTH,
        data: user
      });
    }

    return user;
  };

  /**
   * Initializes the websocket listeners.
   */
  initWebSocket = () => {
    if (!this.socket) {
      return;
    }
    this.socket.on('connected', clientId => {
      this.clientId = clientId;
    });
    this.socket.on('disconnected', () => {
      this.clientId = null;
    });
    this.socket.on('facebook', message => {
      console.log('Received message from social media api');

      switch (message.type) {
        case FacebookResponseTypes.AUTH:
          console.log('Received auth from facebook api');
          if (message.data.accessToken) {
            const date = new Date();
            this.user = {
              id: message.data.id,
              name: message.data.name,
              expiration: date.setSeconds(date.getSeconds() + message.data.expiresIn),
              accessToken: message.data.accessToken,
              permissions: message.data.permissions,
              pages: message.data.pages
            };
            setLocalStorageObject(this.lsKey, this.user);
            if (this.sendResponse) {
              this.sendResponse(message);
            }
          }
          if (this.popup) {
            this.popup.close();
            this.popup = null;
          }
          break;
        case FacebookResponseTypes.IMAGE_UPLOAD:
        case FacebookResponseTypes.VIDEO_UPLOAD:
          this.sendResponse(message);
          break;
        default:
          break;
      }
    });
  };

  /**
   * Spawns the login dialog
   */
  loginDialog = (scope, rerequest) =>
    new Promise((resolve, reject) => {
      if (!this.clientId) {
        return reject('Not connected to social media api service');
      }
      const scopes = Array.isArray(scope) ? scope.join(',') : scope;
      this.popup = window.open(
        `${this.apiUrl}/api/facebook/auth/id/${this.clientId}/${scopes}${
          rerequest ? '?rerequest=true' : ''
        }`,
        '',
        'menubar=no,toolbar=no,resizable=yes,scrollbars=yes,height=600,width=600}'
      );
      resolve();
    });

  /**
   * Locally logs out the user by removing from store.
   */
  logout = () => {
    this.user = null;
    setLocalStorageObject(this.lsKey);
    this.sendResponse({ type: FacebookResponseTypes.AUTH });
  };

  /**
   * Revokes login access to the app
   * This is different than logging out, which is locally tracked.
   * This will revoke app access for that facebook user.
   */
  revokeLogin = () =>
    new Promise((resolve, reject) => {
      if (!this.clientId) {
        return reject('Not connected to social media api service');
      }
      if (!this.isAuthenticated()) {
        return reject('Not authenticated');
      }
      const url = window.location.protocol.toLowerCase().startsWith('https')
      ? `https://${window.location.hostname}/socialmedia`
      : this.apiUrl;
      fetch(`${url}/api/facebook/auth/revoke`, {
        method: 'get',
        headers: this.getHeader()
      })
        .then(response => {
          if (response.ok) {
            this.logout();
            return resolve();
          }
          return response.json().then(json => Promise.reject(json));
        })
        .catch(error => {
          console.log(error);
          return reject(error);
        });
    });

  /**
   * Posts image to feed with an optional caption.
   */
  uploadImage = (filePath, caption, pageId) =>
    new Promise((resolve, reject) => {
      if (!this.user) {
        return reject('No access token detected.  Authenticate and then try again.');
      }

      if (!filePath) {
        return reject('filePath is required');
      }

      if (!pageId) {
        return reject('pageId is required');
      }

      const payload = {
        filePath,
        pageId
      };

      if (caption) {
        payload.caption = caption;
      }

      const url = window.location.protocol.toLowerCase().startsWith('https')
      ? `https://${window.location.hostname}/socialmedia`
      : this.apiUrl;

      fetch(`${url}/api/facebook/upload-image`, {
        method: 'post',
        headers: this.getHeader(pageId),
        body: JSON.stringify(payload)
      })
        .then(response => response.json())
        .then(response => resolve(response))
        .catch(error => {
          console.log(error);
          reject(error);
        });
    });

  /**
   * Uploads video with optional title and description
   */
  uploadVideo = (filePath, title, description, pageId, async) =>
    new Promise((resolve, reject) => {
      if (!this.user) {
        return reject('No access token detected.  Authenticate and then try again.');
      }

      if (!filePath) {
        return reject('filePath is required');
      }

      if (!pageId) {
        return reject('pageId is required');
      }

      const payload = {
        filePath,
        pageId
      };

      if (title) {
        payload.title = title;
      }

      if (description) {
        payload.description = description;
      }

      if (async) {
        payload.async = async;
        resolve();
      }

      fetch(`${this.apiUrl}/api/facebook/upload-video`, {
        method: 'post',
        headers: this.getHeader(pageId),
        body: JSON.stringify(payload)
      })
        .then(response => response.json())
        .then(response => resolve(response))
        .catch(error => {
          console.log(error);
          if (!async) {
            return reject(error.message);
          }
        });
    });

  /**
   * Checks if the user has appropriate facebook permissions
   */
  checkPermissions = permissions => {
    if (!this.user || !this.user.permissions || !permissions || !Array.isArray(permissions)) {
      return false;
    }

    const grantedPermissions = this.user.permissions.filter(p => p.status === 'granted');
    return permissions.some(p => !grantedPermissions.includes(p));
  };

  getAccessTokenForPageById = id => {
    if (this.user && this.user.pages) {
      const page = this.user.pages.find(p => p.id === id);
      if (page) {
        return page.access_token;
      }
    }
    return null;
  };
}

/**
 * Sets local store as json object, or removes it if null.
 */
const setLocalStorageObject = (key, value) => {
  if (value) {
    localStorage.setItem(key, JSON.stringify(value));
  } else {
    localStorage.removeItem(key);
  }
};

/**
 * Gets local store json object.
 */
const getLocalStorageObject = key => {
  const value = localStorage.getItem(key);
  return value && JSON.parse(value);
};



// WEBPACK FOOTER //
// ./app/api/facebook.js